import { useEffect, useMemo, useState } from "react";

const EMPTY_ARRAY = [];
const NOOP = () => {};

const getImageUrl = (image) => {
    if (!image) {
        return "";
    }

    if (typeof image === "string") {
        return image;
    }

    return image.url || image.full_url || image.image || image.path || image.src || "";
};

const getImageKey = (image, index) => {
    if (!image) {
        return index;
    }

    if (image.id) {
        return image.id;
    }

    return getImageUrl(image) || index;
};

const ProductGallery = ({
    existingImages = EMPTY_ARRAY,
    files = EMPTY_ARRAY,
    onExistingImagesChange = NOOP,
    onFilesChange = NOOP,
    onUploadFiles = null,
    onDeleteExistingImage = NOOP,
    onOrderExistingImages = NOOP,
}) => {
    const [filePreviews, setFilePreviews] = useState([]);
    const [actionKey, setActionKey] = useState("");

    useEffect(() => {
        const previews = files.map((file) => ({
            file,
            url: URL.createObjectURL(file),
        }));

        setFilePreviews(previews);

        return () => {
            previews.forEach((preview) => URL.revokeObjectURL(preview.url));
        };
    }, [files]);

    const galleryItems = useMemo(() => {
        return [
            ...existingImages.map((image, index) => ({
                type: "existing",
                key: `existing-${getImageKey(image, index)}`,
                image,
                url: getImageUrl(image),
            })),
            ...filePreviews.map((preview, index) => ({
                type: "file",
                key: `file-${preview.file.name}-${preview.file.lastModified}-${index}`,
                file: preview.file,
                url: preview.url,
            })),
        ];
    }, [existingImages, filePreviews]);

    const setGalleryItems = (items) => {
        onExistingImagesChange(items.filter((item) => item.type === "existing").map((item) => item.image));
        onFilesChange(items.filter((item) => item.type === "file").map((item) => item.file));
    };

    const addFilesHandler = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);

        event.target.value = "";

        if (selectedFiles.length < 1) {
            return;
        }

        if (!onUploadFiles) {
            onFilesChange([...files, ...selectedFiles]);
            return;
        }

        try {
            setActionKey("upload");
            await onUploadFiles(selectedFiles);
        } finally {
            setActionKey("");
        }
    };

    const deleteItemHandler = async (item) => {
        try {
            setActionKey(`delete-${item.key}`);

            if (item.type === "existing") {
                await onDeleteExistingImage(item.image);
            }

            setGalleryItems(galleryItems.filter((galleryItem) => galleryItem.key !== item.key));
        } finally {
            setActionKey("");
        }
    };

    const moveItemHandler = async (index, direction) => {
        const targetIndex = index + direction;

        if (targetIndex < 0 || targetIndex >= galleryItems.length) {
            return;
        }

        const reorderedItems = [...galleryItems];
        const [item] = reorderedItems.splice(index, 1);
        reorderedItems.splice(targetIndex, 0, item);

        try {
            setActionKey(`move-${item.key}`);
            await onOrderExistingImages(
                reorderedItems.filter((galleryItem) => galleryItem.type === "existing").map((galleryItem) => galleryItem.image),
            );
            setGalleryItems(reorderedItems);
        } finally {
            setActionKey("");
        }
    };

    return (
        <div className="product-gallery">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="fw-bold">Галерея</div>
                <label className="btn btn-secondary btn-sm mb-0">
                    {actionKey === "upload" ? "Завантаження" : "Додати фото"}
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        className="d-none"
                        disabled={Boolean(actionKey)}
                        onChange={addFilesHandler}
                    />
                </label>
            </div>

            {galleryItems.length > 0 ? (
                <div className="product-gallery__grid">
                    {galleryItems.map((item, index) => {
                        const isActionLoading = actionKey === `move-${item.key}` || actionKey === `delete-${item.key}`;

                        return (
                            <div
                                className="product-gallery__item"
                                key={item.key}
                            >
                                <img
                                    src={item.url}
                                    alt=""
                                    className="product-gallery__image"
                                />
                                <div className="product-gallery__actions">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm"
                                        title="Вгору"
                                        disabled={index === 0 || Boolean(actionKey)}
                                        onClick={() => moveItemHandler(index, -1)}
                                    >
                                        <i className="bi bi-arrow-up"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm"
                                        title="Вниз"
                                        disabled={index === galleryItems.length - 1 || Boolean(actionKey)}
                                        onClick={() => moveItemHandler(index, 1)}
                                    >
                                        <i className="bi bi-arrow-down"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        title="Видалити"
                                        disabled={Boolean(actionKey)}
                                        onClick={() => deleteItemHandler(item)}
                                    >
                                        <i className={`bi ${isActionLoading ? "bi-hourglass-split" : "bi-trash"}`}></i>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="alert alert-info mb-0">Фото ще не додані</div>
            )}
        </div>
    );
};

export default ProductGallery;
